import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import Image from '../Image/Image'
import ProductMarkers from '../ProductMarkers/ProductMarkers'
import styles from './ProductGallery.module.css'

interface ProductGalleryProps {
  mainPicture: string
  mainPictureBig: string
  images: string[]
  productName: string
  tags: Array<{ id: number; name: string }>
}

export default function ProductGallery({
  mainPicture,
  mainPictureBig,
  images,
  productName,
  tags,
}: ProductGalleryProps) {
  const [selectedImage, setSelectedImage] = useState(0)
  const [isZoomed, setIsZoomed] = useState(false)

  const allImages = [mainPictureBig || mainPicture, ...images.filter(img => img)]
  const currentSrc = allImages[selectedImage]
  const isPrimary = selectedImage === 0

  useEffect(() => {
    if (!isZoomed) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsZoomed(false)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [isZoomed])

  return (
    <div className={styles.gallery}>
      <ProductMarkers tags={tags && Array.isArray(tags) ? tags : []} />
      <div className={`${styles.mainImage} ${selectedImage === 0 ? styles.firstImage : ''}`}>
        <button
          type="button"
          className={styles.zoomTrigger}
          onClick={() => setIsZoomed(true)}
          aria-label={`Збільшити зображення ${productName}`}
        >
          <Image
            mainPicture={isPrimary ? mainPicture : undefined}
            mainPictureBig={isPrimary ? mainPictureBig : undefined}
            src={isPrimary ? undefined : currentSrc}
            alt=""
            priority
          />
        </button>
      </div>

      {allImages.length > 1 && (
        <div className={styles.thumbnails}>
          {allImages.map((image, index) => (
            <button
              type="button"
              key={`${image}-${index}`}
              className={`${styles.thumbnail} ${selectedImage === index ? styles.active : ''}`}
              onClick={() => setSelectedImage(index)}
              aria-label={`${productName} — мініатюра ${index + 1}`}
              aria-pressed={selectedImage === index}
            >
              <Image
                mainPicture={index === 0 ? mainPicture : undefined}
                mainPictureBig={index === 0 ? mainPictureBig : undefined}
                src={index === 0 ? undefined : image}
                alt=""
              />
            </button>
          ))}
        </div>
      )}

      {isZoomed &&
        createPortal(
          <div
            className={styles.zoomOverlay}
            role="dialog"
            aria-modal="true"
            aria-label={`${productName} — збільшене зображення`}
            onClick={() => setIsZoomed(false)}
          >
            <button type="button" className={styles.zoomClose} aria-label="Закрити">
              ×
            </button>
            <Image
              mainPicture={isPrimary ? mainPicture : undefined}
              mainPictureBig={isPrimary ? mainPictureBig : undefined}
              src={isPrimary ? undefined : currentSrc}
              alt={`${productName} — збільшене зображення`}
              className={styles.zoomImage}
              priority
              onClick={event => event.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </div>
  )
}
